import React, { FC, useCallback, useMemo, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import _omit from 'lodash/omit';
import { useToggle } from 'usehooks-ts';

import {
  OrganizationDTO,
  OrganizationDTOStatus,
  OrganizationRequestDTO,
  useAutosuggestionOrganizationsHook,
  useFilterOrganizationsInfinite
} from '@/api/generated';
import PlusIcon from '@/assets/icons/plus.svg?react';
import DataGrid from '@/components/DataGrid/DataGrid';
import { AutocompleteWide } from '@/components/fields/Autocomplete';
import { DropdownSelectOption } from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import Form from '@/components/Form/Form';
import StickyHead from '@/components/StickyHead/StickyHead';
import { COMPOUND_FORM_VALUE_KEYS, DEFAULT_VALUES, URL_CONFIG_MAP } from '@/containers/Organizations/constants';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { usePlatformPermissions } from '@/hooks/permissions/usePlatformPermissions';
import { useDirection } from '@/hooks/useDirection';
import { useEntityList } from '@/hooks/useEntityList';
import { usePageTitle } from '@/hooks/usePageTitle';
import { CREATE_MODAL_PARAM, toOrganizationItems } from '@/root/linker';
import typography from '@/theme/typography';
import CreateOrganizationModal from '@/views/CreateOrganizationModal/CreateOrganizationModal';
import OfficeChipSelect from '@/views/fields/OfficeChipSelect/OfficeChipSelect';
import FilterButton from '@/views/Filters/FilterButton';
import FilterPanel from '@/views/Filters/FilterPanel';
import SectionOrganization from '@/views/Filters/SectionOrganization';

import { organizationColumns, OrganizationDTOWithTalents } from './organizationColumns';

const NewOrganizationButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const { spacing } = useTheme();
  const { t } = useTranslation('filters');
  const { value: { canCreateOrganizations } = {} } = usePlatformPermissions();

  if (!canCreateOrganizations) {
    return null;
  }

  return (
    <Button
      variant="contained"
      onClick={onClick}
      sx={{
        ...typography.body2,
        fontWeight: typography.fontWeightMedium,
        pb: 0.5,
        pt: 0.5,
        paddingInlineStart: 1,
        paddingInlineEnd: 1.5,
        '& .MuiButton-icon': {
          marginInlineStart: 0,
          marginInlineEnd: 1
        },
        '& .PlusIcon-root': { width: spacing(2), height: spacing(2) }
      }}
      startIcon={<PlusIcon className="PlusIcon-root" />}
    >
      {t('filters:filters.new')}
    </Button>
  );
};

const Organizations = () => {
  const theme = useTheme();
  const { t } = useTranslation('organizations');
  const { applyDirectionalStyles } = useDirection();
  const autosuggestOrganizations = useAutosuggestionOrganizationsHook();
  const [showFilters, toggleShowFilters] = useToggle();
  const [searchParams] = useSearchParams();
  const [showCreateModal, toggleShowCreateModal] = useToggle(!!searchParams.get(CREATE_MODAL_PARAM));
  const [draftOrganizationSlug, setDraftOrganizationSlug] = useState<string | null>(null);

  const [queryBody, setQueryBody] = useState<OrganizationRequestDTO | null>(null);
  const { isMidotUser } = useIsMidotUser();

  usePageTitle(t('layouts.organizations.title'));

  const handleEditDraftOrganization = useCallback(
    (draftOrganizationSlug: string) => {
      setDraftOrganizationSlug(draftOrganizationSlug);
      toggleShowCreateModal();
    },
    [toggleShowCreateModal]
  );

  const handleCloseCreateModal = useCallback(() => {
    toggleShowCreateModal();
    setDraftOrganizationSlug(null);
  }, [toggleShowCreateModal]);

  const {
    data: organizationsData,
    isError,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage
  } = useFilterOrganizationsInfinite(queryBody ?? {}, {
    query: { suspense: false, enabled: !!queryBody }
  });

  const organizations = useMemo(
    () => (organizationsData?.pages?.flatMap(page => page) || []) as OrganizationDTOWithTalents[],
    [organizationsData]
  );

  const handleFetchNextPage = useCallback(() => {
    if (isError || isFetchingNextPage || !hasNextPage || !organizationsData?.pages?.length) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage, organizationsData?.pages?.length]);

  const convertFormToRequest = useCallback(
    (formValues: FieldValues): OrganizationRequestDTO =>
      _omit(
        {
          ...formValues,
          organization: {
            ...formValues?.organization,
            office: formValues?.office
          }
        },
        FIELDS_TO_OMIT
      ),
    []
  );

  const { sorting, handleChangeSort, form } = useEntityList<FieldValues, OrganizationRequestDTO>(
    DEFAULT_VALUES as FieldValues,
    URL_CONFIG_MAP,
    setQueryBody,
    convertFormToRequest,
    300
  );

  const getRowProps = useCallback((row: OrganizationDTO) => {
    switch (row.status) {
      case OrganizationDTOStatus.INACTIVE: {
        return {
          sx: {
            filter: 'grayscale(1)',
            color: 'grey.700',
            '& .MuiTypography-root, & .MuiChip-label': {
              color: 'grey.700'
            }
          }
        };
      }
      case OrganizationDTOStatus.DRAFT: {
        return {
          sx: {
            bgcolor: 'grey.100',
            '& .MuiTypography-root': {
              color: 'primary.600'
            },
            '& .ProgressTalentDisplay-root': {
              display: 'none'
            }
          }
        };
      }
      default: {
        return {};
      }
    }
  }, []);

  const handleSearchOrganizations = useCallback(
    async (search: string): Promise<DropdownSelectOption[]> => {
      try {
        const searchResults = await autosuggestOrganizations({ search });

        return searchResults.map(({ name, slug }) => ({
          label: name!,
          value: slug!,
          to: toOrganizationItems({ organizationSlug: slug! })
        }));
      } catch (e) {
        return [];
      }
    },
    [autosuggestOrganizations]
  );

  return (
    <Box sx={{ px: 5, pb: 5, flexGrow: 1 }} component="div">
      <StickyHead>
        <Form className="Filters-root" form={form} sx={{ pt: 2.25 }}>
          <Stack direction="row" mb={3} alignItems="center">
            <Typography variant="h2">{t('layouts.organizations.title')}</Typography>
            {isMidotUser && (
              <Stack direction="row" alignItems="center" sx={applyDirectionalStyles(2.5, 0, 'ml', 'mr')}>
                <Field component={OfficeChipSelect} name="office" />
              </Stack>
            )}
          </Stack>

          <Stack direction="row" justifyContent="space-between" sx={{ marginTop: theme.spacing(3) }}>
            <AutocompleteWide
              placeholder={t('layouts.organizations.autosuggest.orgPlaceholder')}
              loadOptions={handleSearchOrganizations}
            />

            <Stack direction="row" gap={2}>
              <FilterButton
                compoundFormKeys={COMPOUND_FORM_VALUE_KEYS}
                isOpen={showFilters}
                onClick={toggleShowFilters}
              />
              {isMidotUser && <NewOrganizationButton onClick={toggleShowCreateModal} />}
            </Stack>
          </Stack>

          <FilterPanel isOpen={showFilters}>
            <SectionOrganization name="organization" />
          </FilterPanel>
        </Form>
      </StickyHead>
      <DataGrid
        data={organizations}
        columns={organizationColumns(t, handleEditDraftOrganization, isMidotUser)}
        onSort={handleChangeSort}
        isLoading={isLoading || !queryBody}
        isLoadingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        getRowProps={getRowProps}
        sorting={sorting}
        resizeWatchSelector=".Filters-root"
        hasError={isError}
        onLoadNextPage={handleFetchNextPage}
      />

      {showCreateModal && (
        <CreateOrganizationModal
          onClose={handleCloseCreateModal}
          currentFilter={queryBody}
          draftOrganizationSlug={draftOrganizationSlug}
        />
      )}
    </Box>
  );
};

export default Organizations;

const FIELDS_TO_OMIT = ['office', 'process', 'talent'];
